import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import './PdfPreviewModal.css';

// Self-hosted pdf.js worker (audit 2026-05-17). Previously loaded from unpkg.com
// (third-party CDN, supply-chain + GDPR risk). The file in frontend/public/pdf/
// is the pdf.worker.min.js shipped with the pinned pdfjs-dist@3.11.174
// dependency and is refreshed whenever that package is bumped.
const PDF_WORKER_URL = '/pdf/pdf.worker.min.js';

const PdfPreviewModal = ({ isOpen, pdfUrl, onClose, onDownload, fileName }) => {
  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
      return;
    }
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onClose();
    }
  }, [pdfUrl, onDownload, onClose, fileName]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc, true);
    return () => document.removeEventListener('keydown', handleEsc, true);
  }, [isOpen, onClose]);

  if (!isOpen || !pdfUrl) return null;

  // Rendered at the top of the page, not where the host puts it. A host
  // column that stays put while the page scrolls (the delivery's paperwork
  // column) keeps everything inside it on its own layer, so a pop-up left
  // there sits UNDER the delivery's date boxes however high it is raised.
  return createPortal(
    <div className="pdf-preview-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pdf-preview-modal-content">
        <Worker workerUrl={PDF_WORKER_URL}>
          <div className="pdf-preview-modal-viewer-container">
            <Viewer fileUrl={pdfUrl} />
          </div>
        </Worker>
        <div className="pdf-preview-modal-footer">
          <div className="pdf-preview-modal-footer-left">
            <button className="pdf-preview-modal-btn pdf-preview-modal-btn-cancel" onClick={onClose}>Cancel</button>
          </div>
          <div className="pdf-preview-modal-footer-right">
            <button className="pdf-preview-modal-btn pdf-preview-modal-btn-download" onClick={handleDownload}>Download</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfPreviewModal;
